import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';
import { trackerKeys } from './hooks';

/**
 * The next thing somebody said they would do about a lead.
 *
 * Kyu, on why this is a reminder and not a cadence: "Alex changes his mind a
 * lot and the SOP may change. I do not want a hard-coded cadence engine." So
 * nothing here schedules anything. A person logs an email, is offered a date,
 * and either takes it or does not.
 *
 * ── Why these are ordinary tasks rows ──────────────────────────────────────
 *
 * Because they have to reach My Tasks, take an assignee and carry comments.
 * The restructure rejected a sales-only task table on exactly that ground:
 * it "reaches nothing else".
 *
 * The cost of that decision is `tasks_one_parent` — a task belongs to a
 * project or a deal, never neither — and a lead is neither. Hence the single
 * archived holder project, and hence `create_lead_followup` doing the write
 * rather than the browser: resolving the holder, writing the row and setting
 * the assignee are one thing that must not half-happen.
 */

export const followupKeys = {
  open: () => [...trackerKeys.all, 'followups'] as const,
};

export interface LeadFollowup {
  id: string;
  lead_id: string;
  title: string;
  due_date: string | null;
  assignee_id: string | null;
  is_completed: boolean;
}

const FOLLOWUP_COLUMNS = 'id, lead_id, title, due_date, assignee_id, is_completed';

/**
 * The quick dates, and the only place their arithmetic lives.
 *
 * Day-valued from the caller's today rather than derived from a timestamp:
 * "tomorrow" has to mean tomorrow to the person clicking it, and a UTC
 * instant does not for half the world. The same reasoning the activity
 * timeline settled on with `occurred_is_day`.
 */
export const QUICK_DATES = [
  { key: 'tomorrow', label: 'Tomorrow', days: 1 },
  { key: 'two-days', label: 'In 2 days', days: 2 },
  { key: 'next-week', label: 'Next week', days: 7 },
] as const;

export function addDaysIso(todayIso: string, days: number): string {
  const [year, month, day] = todayIso.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`not a date: ${todayIso}`);
  }
  // UTC arithmetic on a bare calendar day. Date.UTC handles month and year
  // ends, which hand-rolled addition gets wrong twice a year.
  const at = new Date(Date.UTC(year, month - 1, day + days));
  return at.toISOString().slice(0, 10);
}

/**
 * How a due date reads, which is not how a last-touch date reads.
 *
 * `lastTouchLabel` counts backwards — Today, Yesterday, N days ago. A follow-up
 * points the other way, and feeding one to the other renders "-3 days ago".
 * Overdue is called out by name because an overdue follow-up is the only
 * state on this column anybody has to act on.
 */
export function dueLabel(dueIso: string, todayIso: string): string {
  const days = Math.round(
    (Date.parse(`${dueIso}T00:00:00Z`) - Date.parse(`${todayIso}T00:00:00Z`)) / 86400000,
  );
  if (days < 0) {
    return days === -1 ? 'Overdue by 1 day' : `Overdue by ${String(-days)} days`;
  }
  if (days === 0) {
    return 'Due today';
  }
  if (days === 1) {
    return 'Tomorrow';
  }
  return `In ${String(days)} days`;
}

export function isOverdue(dueIso: string, todayIso: string): boolean {
  return dueIso < todayIso;
}

/**
 * Every open follow-up the reader is allowed to see.
 *
 * ── Why this does not take a list of lead ids ──────────────────────────────
 *
 * The first version did: `.in('lead_id', leadIds)` with every lead on the
 * page. With 787 leads that is a 31,000-character request URL, and the column
 * came back empty on the live app while the write had plainly succeeded.
 *
 * Enumerating the ids was never necessary. A task with a lead_id IS a lead
 * follow-up — nothing else writes that column — and RLS already limits the
 * rows to what this person may read. Asking for "the open ones" is a short
 * URL, a smaller result, and one fewer thing to keep in step with the table.
 *
 * Bounded by the number of follow-ups nobody has ticked off yet, not by the
 * size of the lead list, so it does not grow with the database.
 */
export async function fetchLeadFollowups(client: SupabaseClient): Promise<LeadFollowup[]> {
  const { data, error } = await client
    .from('tasks')
    .select(FOLLOWUP_COLUMNS)
    .not('lead_id', 'is', null)
    .eq('is_completed', false)
    .order('due_date');
  if (error) {
    throw new Error(error.message);
  }
  return data ?? [];
}

/**
 * Open follow-ups, keyed by lead, soonest first.
 *
 * One row per lead in the map even though a lead may have several: the leads
 * table shows the NEXT action, and the rest are visible on the lead itself.
 */
export function nextActionByLead(followups: LeadFollowup[]): Map<string, LeadFollowup> {
  const byLead = new Map<string, LeadFollowup>();
  for (const followup of followups) {
    const held = byLead.get(followup.lead_id);
    if (held === undefined) {
      byLead.set(followup.lead_id, followup);
      continue;
    }
    // A null due date sorts last: a dated reminder is more urgent than an
    // undated one, and the RPC refuses undated ones anyway.
    if (
      followup.due_date !== null &&
      (held.due_date === null || followup.due_date < held.due_date)
    ) {
      byLead.set(followup.lead_id, followup);
    }
  }
  return byLead;
}

export function useLeadFollowups() {
  const { client } = useAuth();
  return useQuery({
    queryKey: followupKeys.open(),
    queryFn: () => fetchLeadFollowups(client),
    retry: false,
  });
}

export function useCreateLeadFollowup() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      leadId: string;
      due: string;
      assigneeId?: string | null;
      note?: string;
    }): Promise<string> => {
      const { data, error } = await client.rpc('create_lead_followup', {
        p_lead: input.leadId,
        p_due: input.due,
        // Omitted rather than sent as null. The function's default IS null —
        // "assign it to whoever is clicking" — and sending the key would
        // override that default with the same value by a longer route. Same
        // shape log_lead_activity uses for p_occurred_on.
        ...(input.assigneeId == null ? {} : { p_assignee: input.assigneeId }),
        p_note: input.note ?? '',
      });
      if (error) {
        throw new Error(error.message);
      }
      return typeof data === 'string' ? data : '';
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trackerKeys.all });
      // It lands in the assignee's My Tasks too, so that list is stale now.
      // The same invalidation deal tasks do, for the same reason.
      void queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });
}

/**
 * Recently finished follow-ups, for the Completed list on the Tasks tab.
 *
 * Its own query rather than widening the open one, so the column that drives
 * Next action never carries rows it does not use, and neither query grows
 * without a bound: this one is capped, that one is bounded by outstanding
 * work.
 */
export function useCompletedLeadFollowups() {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...followupKeys.open(), 'completed'] as const,
    queryFn: async (): Promise<LeadFollowup[]> => {
      const { data, error } = await client
        .from('tasks')
        .select(FOLLOWUP_COLUMNS)
        .not('lead_id', 'is', null)
        .eq('is_completed', true)
        .order('completed_at', { ascending: false })
        .limit(20);
      if (error) {
        throw new Error(error.message);
      }
      return data ?? [];
    },
    retry: false,
  });
}

/**
 * Completing, rescheduling and reassigning a follow-up.
 *
 * Written against `tasks` directly rather than through an RPC, because unlike
 * creation there is nothing here that has to happen atomically with something
 * else — the holder is already resolved and RLS already decides who may touch
 * the row.
 *
 * The one thing that does need care is the completion shape. `tasks` carries
 * ── Set `is_completed` and NOTHING else about completion ───────────────────
 *
 * `tasks` carries a COLUMN-LEVEL update grant. `authenticated` may write
 * title, description, remarks, section_id, status_id, assignee_id, priority,
 * due_date, position and is_completed — and nothing else. `completed_at` and
 * `completed_by` are stamped by a trigger and, in that migration's words,
 * "carry no browser update privilege at all".
 *
 * The first version of this set all three, reasoning from the completion CHECK
 * constraint that the timestamp had to be supplied. The constraint is real;
 * the conclusion was wrong, because the trigger fills those columns in before
 * the constraint is ever tested. Postgres rejects the statement outright —
 * `permission denied for table tasks`, not a policy failure — so the checkbox
 * failed on the live page while every test passed.
 */
export function useUpdateLeadFollowup() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      taskId: string;
      done?: boolean;
      dueDate?: string;
      assigneeId?: string | null;
    }): Promise<void> => {
      // Built by spreading rather than by assigning onto an empty object, so
      // the result carries a concrete type the client can check instead of a
      // Record it has to be told to trust.
      const changes = {
        ...(input.done === undefined ? {} : { is_completed: input.done }),
        ...(input.dueDate === undefined ? {} : { due_date: input.dueDate }),
        ...(input.assigneeId === undefined ? {} : { assignee_id: input.assigneeId }),
      };
      const { error } = await client.from('tasks').update(changes).eq('id', input.taskId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trackerKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });
}

/** Errors the RPC raises, in words somebody can act on. */
export function followupErrorMessage(raw: string): string {
  if (raw.includes('not_a_sales_member')) {
    return 'You need sales access to set a follow-up.';
  }
  if (raw.includes('followup_needs_a_date')) {
    return 'Pick a date — a follow-up without one never comes due.';
  }
  if (raw.includes('lead_not_found')) {
    return 'That lead no longer exists.';
  }
  if (raw.includes('no_followup_home')) {
    return 'Follow-ups need a TAP admin account to exist. Ask an admin to sign in once.';
  }
  return 'Could not set the follow-up. Try again.';
}
